import React, { useState } from 'react';
import styled from 'styled-components';
import Banner from '../UI/Banner';
import { Background, Main, Text } from '../../styles/themeColors';
import FlexboxContainer from '../UI/FlexboxContainer';
import Button from '../UI/Button';
import { formatDateTime } from '../../utils/formatters';
import { ConsignmentDetails } from 'data/Consignment';
import { CANCELED, SUBSCRIPTION_ACTIVE, SUBSCRIPTION_RENEWAL } from '../../constants/consignmentStates';
import { useAPI } from '../../lib/API';
import { updateSubscriptionState } from '../../requests/consignment';
import { PAYMENT_STATUS } from '../../constants/paymentStatus';
import { TEXT_LABELS } from 'constants/order';
import { PermissionCode } from 'data/UserPermissions';
import { renderToast, toast } from '../UI/ToastNotification';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SubscriptionModificationModal from './Modals/SubscriptionModificationModal';
import SubscriptionCancelModal from './Modals/SubscriptionCancelModal';

const LabelText = styled.span`
  font-size: 16px;
  line-height: 20px;
  color: ${Text.primary};
  padding: 24px 0px 8px 0px;
`;

const DisabledField = styled.div`
  background-color: #E9ECEF;
  border-radius: 6px;
  padding: 8px;
  height: 36px;
  line-height: 20px;
  font-size: 16px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const CancellationInfoBox = styled(FlexboxContainer)`
  background-color: #fff8e1;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 12px;
`;

const CancellationInfoText = styled.span`
  font-size: 14px;
  color: ${Text.primary};
`;

const CancellationReasonText = styled.span`
  font-size: 14px;
  color: ${Text.primary};
  font-style: italic;
`;

interface SubscriptionOrderStatesProps {
  consignmentData?: ConsignmentDetails;
  refreshConsignment?: () => void;
}

const SubscriptionOrderStates: React.FC<SubscriptionOrderStatesProps> = ({ consignmentData, refreshConsignment }) => {
  const [showModificationModal, setShowModificationModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const retryPaymentReq = useAPI({
    deferFn: updateSubscriptionState,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, TEXT_LABELS.ORDER_UPDATED_SUCCESSFULLY);
      refreshConsignment?.();
    },
    onReject: (err: any) => {
      renderToast(toast.TYPE.ERROR, err?.message ?? TEXT_LABELS.UNEXPECTED_ERROR);
    }
  });

  const state = consignmentData?.state;
  const isActive = state === SUBSCRIPTION_ACTIVE || state === SUBSCRIPTION_RENEWAL;
  const isCanceled = state === CANCELED;
  const isRenewalPaymentFailed = state === SUBSCRIPTION_RENEWAL && consignmentData?.paymentStatus === PAYMENT_STATUS.FAILED;
  const isRenewalPaymentNotPaid = state === SUBSCRIPTION_RENEWAL && consignmentData?.paymentStatus === PAYMENT_STATUS.NOT_PAID;
  const isCancellationScheduled = isActive && consignmentData?.availableForRenewal === false;

  if (!isActive && !isCanceled) return null;

  const subscriptionDatesBlock = (
    <FlexboxContainer gap='4px'>
      <FlexboxContainer flexDirection='column' width='200px'>
        <LabelText>{TEXT_LABELS.SUBSCRIPTION_START_DATE}</LabelText>
        <DisabledField>{formatDateTime(consignmentData?.subscriptionStartDate)}</DisabledField>
      </FlexboxContainer>
      <FlexboxContainer flexDirection='column' width='200px'>
        <LabelText>{TEXT_LABELS.SUBSCRIPTION_END_DATE}</LabelText>
        <DisabledField>{formatDateTime(consignmentData?.subscriptionEndDate)}</DisabledField>
      </FlexboxContainer>
    </FlexboxContainer>
  );

  return (
    <>
      <Banner
        bgColor={Background.white}
        boxShadow='0px 8px 16px rgba(47, 66, 83, 0.25), 0px 16px 20px rgba(47, 66, 83, 0.15)'
        title={TEXT_LABELS.SUBSCRIPTION_DETAILS}
      >
        <FlexboxContainer flexDirection='column' width='100%'>
          {isCancellationScheduled && (
            <CancellationInfoBox flexDirection='column' gap='4px'>
              <FlexboxContainer flexDirection='row' alignItems='flex-start' gap='8px'>
                <WarningAmberIcon style={{ color: '#f59e0b', fontSize: 20, marginTop: 1, flexShrink: 0 }} />
                <CancellationInfoText>
                  {TEXT_LABELS.SUBSCRIPTION_CANCELLATION_SCHEDULED_INFO.replace(
                    '{END_DATE}',
                    formatDateTime(consignmentData?.subscriptionEndDate)
                  )}
                </CancellationInfoText>
              </FlexboxContainer>
              {consignmentData?.cancelationRecord?.cancelationMessage && (
                <FlexboxContainer flexDirection='row' gap='4px' padding='0px 0px 0px 28px'>
                  <CancellationInfoText>{TEXT_LABELS.SUBSCRIPTION_CANCELLATION_REASON_LABEL}</CancellationInfoText>
                  <CancellationReasonText>{consignmentData.cancelationRecord.cancelationMessage}</CancellationReasonText>
                </FlexboxContainer>
              )}
            </CancellationInfoBox>
          )}
          <FlexboxContainer width='100%' justifyContent='space-between' alignItems='flex-end'>
            {subscriptionDatesBlock}
            <FlexboxContainer gap='8px'>
              <Button
                disabled={isCanceled || isRenewalPaymentNotPaid || isCancellationScheduled || retryPaymentReq.isLoading}
                bgColor={Main.white}
                color={Main.error}
                borderColor={Main.error}
                onClick={() => setShowCancelModal(true)}
              >
                {TEXT_LABELS.CANCEL_SUBSCRIPTION}
              </Button>
              <Button
                disabled={isCanceled || isRenewalPaymentNotPaid || isCancellationScheduled || retryPaymentReq.isLoading}
                bgColor={Background.navy}
                borderColor={Background.navy}
                onClick={() => setShowModificationModal(true)}
              >
                {TEXT_LABELS.MODIFY_SUBSCRIPTION}
              </Button>
              {isRenewalPaymentFailed && (
                <Button
                  bgColor={Main.brandDark}
                  borderColor={Main.brandDark}
                  permissionCode={PermissionCode.PAYMENT_RESET_RETRY}
                  disabled={retryPaymentReq.isLoading}
                  onClick={() =>
                    retryPaymentReq.run(
                      consignmentData?.consignmentId,
                      SUBSCRIPTION_RENEWAL,
                      consignmentData?.subscriptionStartDate,
                      consignmentData?.subscriptionEndDate,
                      consignmentData?.subscriptionPeriod,
                      PAYMENT_STATUS.NOT_PAID
                    )
                  }
                >
                  {retryPaymentReq.isLoading ? TEXT_LABELS.PROCESSING : TEXT_LABELS.RETRY_PAYMENT}
                </Button>
              )}
            </FlexboxContainer>
          </FlexboxContainer>
        </FlexboxContainer>
      </Banner>
      <SubscriptionModificationModal
        show={showModificationModal}
        onClose={() => setShowModificationModal(false)}
        consignmentId={consignmentData?.consignmentId}
        subscriptionStartDate={consignmentData?.subscriptionStartDate}
        subscriptionEndDate={consignmentData?.subscriptionEndDate}
        onSave={refreshConsignment}
      />
      <SubscriptionCancelModal
        show={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        consignmentId={consignmentData?.consignmentId}
        onSave={refreshConsignment}
      />
    </>
  );
};

export default SubscriptionOrderStates;